import type { QueryClient } from '@tanstack/react-query';
import type { Id, ISODate } from '@contracts/common';
import type { BoardCardSharing } from '@contracts/sharing';
import type { Recipient, RecipientInput } from '@contracts/tasks';
import type {
  BoardDTO, BoardItem, ItemProjectRef, ItemRecord, ItemStakeholderRef, ItemTreeDTO,
} from '@contracts/workplace';
import { inheritedAssignment, type AssignmentLink } from '@domain/workplace-tree';
import type { Forest } from './model';
import { BOARD_KEY } from './useBoard';

/**
 * What a saved item changes in the caches the List reads (the board and any done tree being shown), and what a
 * new card inherits (spec §6.4). Pure apart from `writeRecord`, which only writes to the query cache.
 */

export const TREE_KEY = ['workplace', 'tree'] as const;
export const treeKey = (id: Id) => [...TREE_KEY, id] as const;

/**
 * A saved record as a board card, keeping the card's server-derived counts when it already had some. The record
 * does not carry `blockedSince` (the database sets it): a card that stays blocked keeps the moment it had, and a
 * card that just became blocked has none until the board's next snapshot brings the server's.
 */
export function cardOf(record: ItemRecord, previous?: BoardItem, links: CardLinks = {}, home?: BoardCardSharing): BoardItem {
  const base = {
    id: record.id, parentId: record.parentId, position: record.position, title: record.title,
    version: record.version, lastChangedBy: previous?.lastChangedBy ?? null,
    childTasks: previous?.childTasks ?? { done: 0, total: 0 }, tree: previous?.tree ?? null,
    // A saved record names no links: the card keeps what the board last sent (or what was just sent) until the
    // board's next snapshot brings the server's.
    project: links.project !== undefined ? links.project : previous?.project ?? null,
    stakeholders: links.stakeholders ?? previous?.stakeholders ?? [],
    // Nor its finance links or sources (S5 D154, D155): the card keeps the board's until the next snapshot.
    finance: previous?.finance ?? [], external: previous?.external ?? [], sourced: previous?.sourced ?? false,
    // Nor whether Hana does it or the owner's mission on it (S7 §6.5): kept until the next snapshot.
    assisted: previous?.assisted ?? false,
    ...(previous?.mission === undefined ? {} : { mission: previous.mission }),
    // A saved record names no audience: the card keeps its last known sharing, a new child its parent's (the home's),
    // until the next snapshot.
    sharing: previous?.sharing ?? home ?? { mode: 'private' as const, memberCount: 1, ownerId: '' },
  };
  // A note has no link of its own (020); a task's record says its own.
  if (record.itemType === 'note') return { ...base, strategy: null, itemType: 'note' };
  const stillBlocked = record.blocked && previous?.itemType === 'task' && previous.blocked;
  return {
    ...base, strategy: record.strategyLink, itemType: 'task', recipient: record.recipient, deadline: record.deadline, priority: record.priority,
    status: record.status, blocked: record.blocked, blocker: record.blocker,
    blockedSince: stillBlocked ? previous.blockedSince : null, completedAt: record.completedAt,
  };
}

/** Replaces the card the record describes, or appends it when the list does not hold it yet. */
export function withRecord(items: readonly BoardItem[], record: ItemRecord, links: CardLinks = {}): BoardItem[] {
  const at = items.findIndex((item) => item.id === record.id);
  if (at < 0) return [...items, cardOf(record, undefined, links, items.find((item) => item.id === record.parentId)?.sharing)];
  return items.map((item, index) => (index === at ? cardOf(record, item, links) : item));
}

/**
 * Writes a saved record into the board and every cached tree that holds its card. `links` are the project and
 * stakeholders the save sent, which the record itself does not carry (a new card's, or a changed card's).
 */
export function writeRecord(
  queryClient: QueryClient, record: ItemRecord, { add = false, links = {} }: { add?: boolean; links?: CardLinks } = {},
): void {
  const board = queryClient.getQueryData<BoardDTO>(BOARD_KEY);
  if (board !== undefined && (add || board.items.some((item) => item.id === record.id))) {
    queryClient.setQueryData<BoardDTO>(BOARD_KEY, { ...board, items: withRecord(board.items, record, links) });
  }
  for (const [key, tree] of queryClient.getQueriesData<ItemTreeDTO>({ queryKey: TREE_KEY })) {
    if (tree === undefined) continue;
    const holds = tree.items.some((item) => item.id === record.id || item.id === record.parentId);
    if (holds) queryClient.setQueryData<ItemTreeDTO>(key, { ...tree, items: withRecord(tree.items, record, links) });
  }
}

/** A card's project and stakeholders as a save sends them, already named (projects & stakeholders spec §5.4). */
export type CardLinks = { project?: ItemProjectRef | null; stakeholders?: ItemStakeholderRef[] };

/** The optimistic card of a link change: the project and stakeholders the save sends, before the server answers. */
export function withLinks(card: BoardItem, links: CardLinks): BoardItem {
  return {
    ...card,
    ...(links.project !== undefined ? { project: links.project } : {}),
    ...(links.stakeholders !== undefined ? { stakeholders: links.stakeholders } : {}),
  };
}

export const recipientInputOf = (recipient: Recipient): RecipientInput => ({ kind: 'user', userId: recipient.userId });

export type AssignmentBlock = { recipient: RecipientInput; deadline: ISODate };

/**
 * The block a new task under `parentId` starts with (spec §6.4): the recipient and deadline of the nearest task at
 * or above the parent, skipping notes; at the top level (a new root) it is this person, due `day + 30`. The rule is
 * `inheritedAssignment` (D194), which Hana's drafts follow too; this only builds the chain from the board.
 */
export function inheritedBlock(forest: Forest, parentId: Id | null, me: Id, day: ISODate): AssignmentBlock {
  const chain: AssignmentLink<RecipientInput>[] = [];
  for (let node = parentId === null ? undefined : forest.byId.get(parentId); node !== undefined && node !== null;
    node = node.parent ?? undefined) {
    const item = node.item;
    chain.push(item.itemType === 'task'
      ? { itemType: 'task', recipient: recipientInputOf(item.recipient), deadline: item.deadline }
      : { itemType: 'note', recipient: null, deadline: null });
  }
  return inheritedAssignment<RecipientInput>(chain, { kind: 'user', userId: me }, day);
}
